import { useEffect, useState, useRef } from 'react';
import { Container, Content, Mi4u, NeuronMeet } from './styled';
import { useVideoCall } from '../../contexts/videoCall';
import { useChat } from '../../contexts/chat';
import { Controls } from './controls';
import { MdOutlineContentCopy, MdDragIndicator } from 'react-icons/md';
import { BsFillCameraVideoFill } from 'react-icons/bs';
import { toast } from 'react-toastify';


const WINDOW_MODE = {
  DEFAULT: 'DEFAULT',
  COLLAPSED: 'COLLAPSED'
}

export function VideoCall({ mode, refWindow }) {

  const { dataCall } = useVideoCall();
  const { accessLevel } = useChat();

  const [windowMode, setWindowMode] = useState(WINDOW_MODE.DEFAULT);
  const [position, setPosition] = useState(null);
  const [size, setSize] = useState({ width: 380, height: 520 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const containerRef = useRef(null);

  const isNeuronMeet = dataCall?.call?.links?.user.includes("meet.google.com")


  useEffect(() => {
    if (dataCall?.call?.links?.user.includes("meet.google.com")) {
      window.open(dataCall?.call?.links?.user, '_blank');
    }
  }, [dataCall?.call?.links?.user])
  useEffect(() => {
    if (refWindow) {
      setWindowMode(WINDOW_MODE.COLLAPSED)
    }
  }, [refWindow])


  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(dataCall?.call?.links?.user);
      toast.success('Link copiado para área de transferência', {
        position: "top-right",
        autoClose: 3000,
        icon: <MdOutlineContentCopy />
      });
    } catch (error) {
      console.error('Erro ao copiar link:', error);
      toast.error('Erro ao copiar link');
    }
  };


  useEffect(() => {
    setWindowMode(WINDOW_MODE.DEFAULT)
  }, [dataCall])

  const onToggleWindowMode = () => {
    const newMode = windowMode === WINDOW_MODE.DEFAULT
      ? WINDOW_MODE.COLLAPSED
      : WINDOW_MODE.DEFAULT

    setWindowMode(newMode)

    // Reset position and size when going back to default mode
    if (newMode === WINDOW_MODE.DEFAULT) {
      setPosition(null);
      setSize({ width: 380, height: 520 });
    }
  }

  const handleDragBarMouseDown = (e) => {
    if (windowMode !== WINDOW_MODE.COLLAPSED) return;

    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // Se não há posição definida, use a posição atual do elemento
    const currentPosition = position || {
      x: containerRef.current?.offsetLeft || 20,
      y: containerRef.current?.offsetTop || 20
    };

    setDragStart({
      x: clientX - currentPosition.x,
      y: clientY - currentPosition.y
    });

    // Define a posição inicial se ainda não foi definida
    if (!position) {
      setPosition(currentPosition);
    }
  };



  const handleResizeMouseDown = (e) => {
    if (windowMode !== WINDOW_MODE.COLLAPSED) return;

    e.preventDefault();
    e.stopPropagation();

    setIsResizing(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    setResizeStart({
      x: clientX,
      y: clientY,
      width: size.width,
      height: size.height
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  useEffect(() => {
    const handleMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      if (isDragging && windowMode === WINDOW_MODE.COLLAPSED) {
        const newX = clientX - dragStart.x;
        const newY = clientY - dragStart.y;

        // Limites da tela para drag
        const maxX = window.innerWidth - size.width;
        const maxY = window.innerHeight - size.height;

        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }

      if (isResizing && windowMode === WINDOW_MODE.COLLAPSED) {
        const deltaX = clientX - resizeStart.x;
        const deltaY = clientY - resizeStart.y;

        const newWidth = Math.max(300, Math.min(800, resizeStart.width + deltaX));
        const newHeight = Math.max(260, Math.min(600, resizeStart.height + deltaY));

        setSize({
          width: newWidth,
          height: newHeight
        });

        // Ajustar posição se necessário para não sair da tela
        if (position) {
          const maxX = window.innerWidth - newWidth;
          const maxY = window.innerHeight - newHeight;

          setPosition(prev => ({
            x: Math.max(0, Math.min(prev.x, maxX)),
            y: Math.max(0, Math.min(prev.y, maxY))
          }));
        }
      }
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleMove);
      document.addEventListener('touchend', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStart, resizeStart, windowMode, size, position]);

  if (dataCall?.call?.status !== "IN_CALL") {
    return null
  }

  return (
    <Container
      ref={containerRef}
      mode={mode}
      windowMode={windowMode}
      position={position}
      size={size}
      isDragging={isDragging}
      isResizing={isResizing}
    >
      {windowMode === WINDOW_MODE.COLLAPSED && (
        <>
          <div
            className="drag-bar"
            onMouseDown={handleDragBarMouseDown}
            onTouchStart={handleDragBarMouseDown}
            title="Clique e arraste para mover a janela"
          >
            <MdDragIndicator />
          </div>
          <div
            className="resize-handle"
            onMouseDown={handleResizeMouseDown}
            onTouchStart={handleResizeMouseDown}
            title="Clique e arraste para redimensionar"
          />
        </>
      )}
      <Content isDragging={isDragging} windowMode={windowMode} accessLevel={accessLevel}>
        {isNeuronMeet
          ? <NeuronMeet accessLevel={accessLevel}>
            <div className="call-animation">
              <div className="call-icon">
                <BsFillCameraVideoFill />
              </div>
              <div className="pulse-rings">
                <div className="ring ring-1"></div>
                <div className="ring ring-2"></div>
                <div className="ring ring-3"></div>
              </div>
            </div>
            <div className="call-info">
              <h3>Chamada em andamento</h3>
              <p>A videochamada está sendo realizada em uma nova guia pelo link a baixo</p>
              <div className='link-wrapper'>
                <div className="link-container">
                  <input
                    type="text"
                    value={dataCall?.call?.links?.user || ''}
                    readOnly
                    className="link-input"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="copy-button"
                    title="Copiar link"
                  >
                    <MdOutlineContentCopy />
                  </button>
                </div>
              </div>
            </div>
          </NeuronMeet>
          : <Mi4u
            title="call--video"
            allow='camera *;microphone *;display-capture *;'
            src={dataCall?.call?.links?.user}
          />
        }
        <Controls
          isNeuronMeet={isNeuronMeet}
          onToggleWindowMode={onToggleWindowMode}
          windowMode={windowMode}
          accessLevel={accessLevel}
        />
      </Content>
    </Container>
  )
}